import { useState } from 'react';

import { EASINGS, Editor, stopEventPropagation, track, useEditor } from 'tldraw';

import { VisualChronologyNodeShape } from '../TimelineElements/VisualChronologyNode';
import { cn } from '@/helpers/shadCnUtils';
import { Icon } from '@/new/components/icon';
import { Input } from '@/new/components/shadcn/input';

interface SearchResult {
  text: string;
  shape: VisualChronologyNodeShape;
}

function moveToShape(editor: Editor, shape: VisualChronologyNodeShape) {
  const bounds = editor.getShapePageBounds(shape.id);
  if (!bounds) return;
  editor.setSelectedShapes([shape.id]);
  editor.zoomToSelection({
    animation: { duration: 500, easing: EASINGS.easeInOutCubic },
  });
}

function getShapesWithText(editor: Editor, text: string): SearchResult[] {
  if (!text || text.length === 0) return [];
  const shapes = editor.getCurrentPageShapes().filter((shape) => shape.type === 'visual-chronology-node');
  const result: SearchResult[] = [];

  shapes.forEach((shape) => {
    const s = shape as VisualChronologyNodeShape;
    const date = s.props.date ?? '';
    const description = s.props.description ?? '';
    const shapeText = (date ? date + ': ' : '') + description;
    if (shapeText.toLowerCase().includes(text.toLowerCase())) {
      result.push({ text: shapeText, shape: s });
    }
  });
  return result;
}

export const SearchBar = track(() => {
  const editor = useEditor();
  const [searchText, setSearchText] = useState('');

  const results = getShapesWithText(editor, searchText);
  return (
    <div className="gap- pointer-events-auto flex-col" onPointerDown={(e) => stopEventPropagation(e)}>
      <div className="flex w-64 items-center gap-2">
        <div className="relative flex w-full items-center">
          <Icon className="lucide--search absolute left-3 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search..."
            className="w-full rounded-md border-gray-200 bg-white pl-9 shadow-sm transition-all focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            onChange={(e) => setSearchText(e.target.value)}
            value={searchText}
          />
        </div>
      </div>
      {searchText && (
        <div className="absolute mt-2 flex max-h-72 flex-col overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-sm">
          {results.map((result, index) => {
            return (
              <div
                key={'text-search-panel-button:' + result.shape.id}
                className={cn(
                  'flex w-[600px] cursor-pointer flex-col gap-1.5 border-b border-gray-100 p-3 transition-colors hover:bg-gray-50',
                  {
                    'border-b-0': index === results.length - 1,
                  },
                )}
                onClick={() => {
                  moveToShape(editor, result.shape);
                  setSearchText('');
                }}
              >
                <div className="line-clamp-2 text-xs text-gray-600">{result.text.slice(0, 100)}...</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
});
